import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Navbar from "../../../components/Navbar";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ChevronRight, 
  ArrowLeft, 
  UserCog, 
  Save,
  AlertCircle,
  Mail,
  Phone,
  User,
  Shield,
  AlertTriangle,
  ChevronDown,
  Search,
  Check
} from "lucide-react";
import { useTeamManagement } from "../../../hooks/permissionHooks/useTeamManagement";
import { useCustomRoles } from "../../../hooks/permissionHooks/useCustomRoles";
import { useTeamValidation } from "../../../hooks/permissionHooks/useTeamValidation";
import { canEditTeamMembers } from "../../../utils/rolesPermissions";
import { toast } from "react-toastify";

const RoleDropdown = ({ 
  roles, 
  selectedRole, 
  onSelect, 
  error, 
  touched 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  const filteredRoles = roles.filter(role =>
    role.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (role.description && role.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const selectedRoleData = roles.find(role => role.name === selectedRole);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <motion.button
        type="button"
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-left flex items-center justify-between ${
          error && touched 
            ? 'border-red-300 bg-red-50' 
            : isOpen 
              ? 'border-[#FF6C6C] bg-[#FF6C6C]/5' 
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
        }`}
      >
        <div className="flex-1">
          {selectedRoleData ? (
            <div>
              <h3 className="font-medium text-gray-800">{selectedRoleData.name}</h3>
              <div className="flex gap-4 items-center mt-1 text-xs text-gray-400">
                <span>{Object.keys(selectedRoleData.permissions || {}).length} modules</span>
                <span>{Object.values(selectedRoleData.permissions || {}).flat().length} permissions</span>
              </div>
            </div>
          ) : (
            <div className="text-gray-500">
              <span>Select a role for this team member</span>
            </div>
          )}
        </div>
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="overflow-hidden absolute z-50 mt-2 w-full max-h-80 bg-white rounded-xl border border-gray-200 shadow-xl"
          >
            {/* Search - Only show if there are many roles */}
            {roles.length > 5 && (
              <div className="p-3 border-b border-gray-100">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search roles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C]"
                  />
                </div>
              </div>
            )}

            {/* Roles List */}
            <div className={`overflow-y-auto ${roles.length > 8 ? 'max-h-64' : 'max-h-80'}`}>
              {filteredRoles.length > 0 ? (
                filteredRoles.map((role) => {
                  const isSelected = selectedRole === role.name;
                  const totalPermissions = Object.values(role.permissions || {}).flat().length;
                  
                  return (
                    <motion.button
                      key={role.id}
                      type="button"
                      whileHover={{ backgroundColor: "#f9fafb" }}
                      onClick={() => {
                        onSelect(role.name);
                        setIsOpen(false);
                        setSearchTerm("");
                      }}
                      className={`w-full p-2.5 text-left transition-all duration-200 flex items-center justify-between hover:bg-gray-50 ${
                        isSelected 
                          ? 'bg-[#FF6C6C]/5 border-l-4 border-[#FF6C6C]' 
                          : ''
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center">
                          <h4 className={`font-medium truncate text-sm ${isSelected ? 'text-[#FF6C6C]' : 'text-gray-800'}`}>
                            {role.name}
                          </h4>
                          {isSelected && (
                            <Check className="w-3.5 h-3.5 text-[#FF6C6C] flex-shrink-0 ml-2" />
                          )}
                        </div>
                        <div className="flex gap-3 items-center mt-0.5 text-xs text-gray-400">
                          <span>{Object.keys(role.permissions || {}).length} modules</span>
                          <span>{totalPermissions} permissions</span>
                        </div>
                      </div>
                    </motion.button>
                  );
                })
              ) : (
                <div className="p-4 text-center text-gray-500">
                  <p>No roles found matching "{searchTerm}"</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {error && touched && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-2 text-sm text-red-600"
        >
          {error}
        </motion.p>
      )}
    </div>
  );
};

const EditTeamMember = () => {
  const navigate = useNavigate();
  const { memberId } = useParams();
  const { isMainVendor, userPermissions } = useSelector((state) => state.user);
  const { 
    teamMembers, 
    updateTeamMember, 
    isUpdating, 
    formatPhoneForApi
  } = useTeamManagement();
  const { customRoles, hasRoles } = useCustomRoles();
  const { validateTeamMemberData } = useTeamValidation();

  // Check if user can edit team members
  const canEdit = canEditTeamMembers(userPermissions, isMainVendor);

  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    role: '',
    roleSlug: '',
    vendorSlug: ''
  });

  const [originalData, setOriginalData] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  //Todo => Get the team member being edited
  const teamMember = teamMembers.find(member => member.id === parseInt(memberId));
  //Todo => Initialize form data when team member is loaded
  useEffect(() => {
    if (teamMember) {
      const memberRole = customRoles.find(role => role.name === teamMember.role);
      const initialData = {
        fullName: teamMember.fullName || teamMember.name || '',
        email: teamMember.email || '',
        phone: teamMember.phone || '',
        role: teamMember.role || '',
        roleSlug: memberRole?.role_slug || '',
        vendorSlug: teamMember.vendorSlug || ''
      };
      setFormData(initialData);
      setOriginalData(initialData);
    }
  }, [teamMember, customRoles]);


  //Todo => Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  //Todo => Handle role selection
  const handleRoleSelect = (roleName) => {
    const selectedRole = customRoles.find(role => role.name === roleName);
    setFormData(prev => ({
      ...prev,
      role: roleName,
      roleSlug: selectedRole?.role_slug || ''
    }));

    if (errors.role) {
      setErrors(prev => ({ ...prev, role: null }));
    }
  };

  //Todo => Validation
  const validateForm = () => {
    const validation = validateTeamMemberData({ 
      ...formData, 
      role_slug: formData.roleSlug, // Add role_slug for validation
      id: parseInt(memberId)
    });
    setErrors(validation.errors);
    return validation.isValid;
  };

  //Todo => Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();    
    
    if (!validateForm()) {
      toast.error("Please fix the form errors");
      return;
    }

    try {
      const formattedPhone = formatPhoneForApi ? formatPhoneForApi(formData.phone) : formData.phone;      
      const payload = {
        full_name: formData.fullName,
        phone_number: formattedPhone,
        email: formData.email,
        role_slug: formData.roleSlug,
        vendor_slug: formData.vendorSlug
      };

      
      // Check if any required fields are missing
      const missingFields = [];
      if (!payload.full_name) missingFields.push('full_name');
      if (!payload.phone_number) missingFields.push('phone_number');
      if (!payload.email) missingFields.push('email');
      if (!payload.role_slug) missingFields.push('role_slug');
      if (!payload.vendor_slug) missingFields.push('vendor_slug');
      
      if (missingFields.length > 0) {
        toast.error(`Missing required fields: ${missingFields.join(', ')}`);
        return;
      }
      
      const result = await updateTeamMember(payload);
      
      
      // Check if the result has validation errors
      if (!result.success && result.errors) {
        
        // Map API field names to form field names
        const fieldMapping = {
          'full_name': 'fullName',
          'phone_number': 'phone',
          'email': 'email',
          'role_slug': 'role',
          'vendor_slug': 'vendorSlug'
        };
        
        // Set validation errors
        const apiErrors = {};
        Object.keys(result.errors).forEach(apiField => {
          const formField = fieldMapping[apiField] || apiField;
          apiErrors[formField] = Array.isArray(result.errors[apiField]) 
            ? result.errors[apiField][0] 
            : result.errors[apiField];
        });
        
        setErrors(apiErrors);
        toast.error(result.message || "Please fix the validation errors");
        return;
      }
      
      if (result.success) {
        toast.success("Team member updated successfully!");
        navigate('/roles');
      } else {
        toast.error(result.message || "Update failed");
      }
      
    } catch (error) {
      toast.error("Update failed. Please try again.");
    }
  };

  //Todo => Handle field blur
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  //Todo => Check permissions
  if (!canEdit) {
    return (
      <div className="bg-white">
        <Navbar page={"Edit Team Member"} />
        <div className="flex justify-center items-center min-h-screen bg-gray-50">
          <div className="text-center">
            <AlertCircle className="mx-auto mb-4 w-16 h-16 text-gray-400" />
            <h2 className="mb-2 text-xl font-semibold text-gray-800">Access Restricted</h2>
            <p className="text-gray-500">You don't have permission to edit team members.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!teamMember) {
    return (
      <div className="bg-white">
        <Navbar page={"Edit Team Member"} />
        <div className="flex justify-center items-center min-h-screen bg-gray-50">
          <div className="text-center">
            <AlertCircle className="mx-auto mb-4 w-16 h-16 text-gray-400" />
              <h2 className="mb-2 text-xl font-semibold text-gray-800">Team Member Not Found</h2>
              <p className="text-gray-500">The team member you're looking for doesn't exist.</p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/roles')}
              className="mt-4 px-6 py-3 text-white bg-gradient-to-r from-[#FF6C6C] to-pink-500 rounded-xl hover:from-[#FF6C6C]/90 hover:to-pink-600 transition-all duration-200 font-medium"
            >
              Back to Team
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  if (!hasRoles) {
    return (
      <div className="bg-white">
        <Navbar page={"Edit Team Member"} />
        <div className="p-6 min-h-screen bg-gray-50">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="max-w-md text-center">
              <div className="inline-block p-4 mb-4 bg-amber-100 rounded-full">
                <AlertCircle className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="mb-2 text-xl font-semibold text-gray-800">No Roles Available</h2>
              <p className="mb-6 text-gray-500">
                You need to have at least one role to edit team members.
              </p>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/roles/create')}
                className="px-6 py-3 text-white bg-gradient-to-r from-[#FF6C6C] to-pink-500 rounded-xl hover:from-[#FF6C6C]/90 hover:to-pink-600 transition-all duration-200 font-medium"
              >
                Create Your First Role
              </motion.button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <Navbar page={"Edit Team Member"} />
      
      <div className="p-6 min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex gap-2 items-center mb-6 text-sm text-gray-500"
        >
          <span 
            className="hover:text-[#FF6C6C] cursor-pointer transition-colors"
            onClick={() => navigate('/dashboard')}
          >
            Dashboard
          </span>
          <ChevronRight size={16} />
          <span 
            className="hover:text-[#FF6C6C] cursor-pointer transition-colors"
            onClick={() => navigate('/roles')}
          >
            Roles & Permissions
          </span>
          <ChevronRight size={16} />
          <span className="text-[#FF6C6C] font-medium">Edit Team Member</span>
        </motion.div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex gap-3 items-center sm:gap-4"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/roles')}
                className="p-2 text-gray-400 rounded-xl transition-all duration-200 hover:text-gray-600 hover:bg-gray-100"
              >
                <ArrowLeft className="w-5 h-5" />
              </motion.button>
              <div className="flex gap-3 items-center">
                <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-[#FF6C6C]/10 via-pink-50 to-white">
                  <UserCog className="w-5 h-5 sm:w-6 sm:h-6 text-[#FF6C6C]" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-800 sm:text-2xl">Edit Team Member</h1>
                  <p className="text-xs text-gray-500 sm:text-sm">Modify team member details and role</p>
                </div>
              </div>
            </motion.div>

            {/* Current Status - Mobile responsive */}
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-3 sm:items-center">
              <div className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium self-start ${
                teamMember.status === 'active'|| teamMember.status === "Active" 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                {teamMember.status || 'Active'}
              </div>
              <span className="text-xs text-gray-500 sm:text-sm">
                <span className="font-medium">Current Role:</span> {teamMember.role}
              </span>
            </div>
          </div>
        </div>

        {/* Role Change Warning */}
        {originalData && formData.role !== originalData.role && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 mb-6 bg-amber-50 rounded-xl border border-amber-200"
          >
            <div className="flex gap-3 items-start">
              <AlertTriangle className="mt-0.5 w-5 h-5 text-amber-600" />
              <div>
                <h3 className="mb-1 font-medium text-amber-800">Role Change Warning</h3>
                <p className="text-sm text-amber-700">
                  Changing the role will update this team member's permissions from{' '}
                  <strong>{originalData.role}</strong> to <strong>{formData.role}</strong>.
                  This change will take effect immediately after saving.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={handleSubmit}
          className="mx-auto max-w-7xl"
        >
          <div className="space-y-8">
            {/* Personal Information */}
            <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="flex gap-3 items-center mb-6">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-800">Personal Information</h2>
              </div>
              
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    onBlur={() => handleBlur('fullName')}
                    placeholder="Enter full name"
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] ${
                      errors.fullName && touched.fullName ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
                    }`}
                  />
                  {errors.fullName && touched.fullName && (
                    <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>
                  )}
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      onBlur={() => handleBlur('email')}
                      placeholder="Enter email address"
                      className={`w-full pl-10 pr-4 py-3 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] ${
                        errors.email && touched.email ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
                      }`}
                    />
                  </div>
                  {errors.email && touched.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Phone Number <span className="text-gray-400">(Optional)</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      onBlur={() => handleBlur('phone')}
                      placeholder="Enter phone number"
                      className={`w-full pl-10 pr-4 py-3 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] ${
                        errors.phone && touched.phone ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
                      }`}
                    />
                  </div>
                  {errors.phone && touched.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Role Assignment */}
            <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="flex gap-3 items-center mb-6">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Shield className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Role Assignment</h2>
                  <p className="text-sm text-gray-500">Change the role for this team member</p>
                </div>
              </div>

              {errors.role && (
                <div className="p-3 mb-4 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-600">{errors.role}</p>
                </div>
              )}

              <RoleDropdown
                roles={customRoles}
                selectedRole={formData.role}
                onSelect={handleRoleSelect}
                error={errors.role}
                touched={touched.role}
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-4 justify-end items-center p-6 mt-8 bg-white rounded-xl border border-gray-100 shadow-sm">
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/roles')}
              className="px-6 py-3 font-medium text-gray-600 bg-white rounded-xl border border-gray-200 transition-all duration-200 hover:bg-gray-50 hover:border-gray-300"
            >
              Cancel
            </motion.button>
            
            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={isUpdating}
              className="px-6 py-3 text-white bg-gradient-to-r from-[#FF6C6C] to-pink-500 rounded-xl hover:from-[#FF6C6C]/90 hover:to-pink-600 transition-all duration-200 font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdating ? (
                <>
                  <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent" />
                  Updating...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Update Team Member
                </>
              )}
            </motion.button>
          </div>
        </motion.form>
      </div>
    </div>
  );
};

export default EditTeamMember; 